import React from 'react';
import styled from 'styled-components';
import { IconVideo } from '@bynder/icons';
import { Translate, useTranslate } from '@bynder/localization';
import unknownSVG from '../../assets/icons/unknown.svg?raw';
import { StyledMediaWidget } from '../shared/styled-components/Widgets';
import { EmptyWidget } from './EmptyWidget';
import { svgtob64 } from '../../helpers/utils';
import { useGuideStore } from '../../stores/guideStore';
import { useWidgetStyleProperty } from '../guides/Inspector/helpers';
import { CSS_PROPERTIES } from '../guides/Inspector/constants';

interface Props {
  assetId: string;
  widgetId: string;
  columnIndex: number;
  sectionId: string;
  readOnly: boolean;
  videoUrl?: string;
  coverUrl?: string;
  controls?: boolean;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  widgetStyle: string;
}

const VideoWidget = ({
  readOnly,
  videoUrl,
  coverUrl,
  controls = true,
  autoplay,
  loop,
  widgetStyle,
}: Props) => {
  const { translate } = useTranslate();
  const isEmpty = !videoUrl || !videoUrl.length;

  const isEditMode = useGuideStore(s => s.isEditMode);
  const { value: borderRadius } = useWidgetStyleProperty(
    widgetStyle,
    'widgets.media.borderRadius',
    CSS_PROPERTIES.BORDER_RADIUS,
  );
  const videoEl = React.useRef(null);

  if (readOnly && isEmpty) {
    return null;
  }

  const getVideo = () => (
    <video
      autoPlay={autoplay}
      loop={loop}
      muted={autoplay}
      poster={coverUrl}
      controlsList="nodownload"
      ref={videoEl}
      controls={controls}
    >
      <source src={videoUrl} type="video/mp4" />
      <Translate id="VIDEO_WIDGET.VIDEO_ERROR" />
    </video>
  );

  const renderVideoWidget = () => {
    if (readOnly) {
      return getVideo();
    }

    if (isEmpty) {
      return (
        <StyledModalTrigger>
          <IconVideo />
        </StyledModalTrigger>
      );
    }

    return <StyledModalTrigger>{getVideo()}</StyledModalTrigger>;
  };

  if (isEmpty && isEditMode) {
    return (
      <EmptyWidget
        icon={<IconVideo />}
        text={translate('WIDGETS_CONSTANTS.EMPTY_VIDEO_WIDGET')}
      />
    );
  }

  return (
    <StyledMediaWidget
      $isEmpty={isEmpty}
      $hasPreview
      $placeholder={svgtob64(unknownSVG)}
      $borderRadius={borderRadius}
    >
      {renderVideoWidget()}
    </StyledMediaWidget>
  );
};

export default VideoWidget;

const StyledModalTrigger = styled.div`
  min-height: 236px;
`;
